import { ApiError } from '@/services/apiClient'

/**
 * SA-769: the sentence for an `oauth_error` the OAuth callback put on the
 * Mailboxes redirect. Codes are ours (`no_refresh_token`, `unknown_mailbox`,
 * SA-755's `account_mismatch` / `account_unverified`) or the IdP's
 * (`access_denied`, …). A bare code is never printed. A Map, not an object
 * literal, so a code such as `constructor` cannot reach a prototype member.
 * SA-947: the WhatsApp group source's refusals too, and `refusalText` for a
 * `{code, message}` detail.
 */
const SENTENCES = new Map<string, string>([
  [
    'no_refresh_token',
    'The provider issued no refresh token, so this mailbox would have stopped within an hour. Nothing was saved. Connect again.',
  ],
  ['backload_loading', 'Cancel the earlier mail that is loading first.'],
  ['access_denied', 'Access was not granted, so the mailbox was not connected.'],
  ['unknown_mailbox', 'The mailbox was removed while it was being connected. Nothing was saved.'],
  ['account_unverified', 'We could not confirm which account signed in, so nothing was saved. Connect again.'],
  // SA-947 (spec P11): the WhatsApp group source's refusals.
  ['waha_credentials_missing', 'Choose a WAHA connection that holds the WAHA base URL and API key.'],
  ['group_source_needs_router', 'A WhatsApp group source is always a router: its messages are triaged.'],
  ['email_field_on_group_source', 'A WhatsApp group source has no email address or prefill mapping.'],
  ['credential_set_not_for_provider', 'Only a WhatsApp group source names a WAHA connection.'],
  ['outbound_unsupported', 'A WhatsApp group source sends nothing, so outbound stays off.'],
  ['not_a_group_source', 'That action is for a WhatsApp group source.'],
  ['invalid_phone_number', "Type the desk's phone number with its country code."],
  ['unknown_group', 'The desk is not in that group any more. Refresh the list and choose again.'],
  ['not_connected', 'Connect the source first.'],
  ['session_not_working', 'The desk is not linked yet. Finish linking, then choose the group.'],
  ['group_already_bound', 'A group is already bound. Use Change group to pick another.'],
  ['waha_not_ready', 'The WhatsApp session is not waiting for a link yet. Wait a moment, then try again.'],
  ['waha_auth_failed', "WAHA refused the API key. Check the WAHA connection's credential set, then connect again."],
  ['waha_unreachable', 'WAHA did not answer. Check that it is running, then try again.'],
  ['waha_error', 'WAHA answered with an error. Its log says why.'],
  ['provider_not_enabled', 'That source type is not enabled on this deployment.'],
  ['credential_set_owned_by_source', "Choose the deployment's WAHA connection, not the set another source made for itself."],
])

const FALLBACK = 'The connect did not complete. Try again.'

/** SA-755: a mismatch's two addresses ride the same redirect. */
export interface ConnectErrorParams {
  expected?: string | null
  account?: string | null
  /** SA-947: the server's own sentence, from a `{code, message}` refusal. */
  message?: string | null
  /** SA-1422: what the sign-in was checked against: connected | typed | named. */
  basis?: string | null
  /** SA-1422: the display name of the mailbox that already reads `account`. */
  otherName?: string | null
}

// SA-947 (spec P11): the server's sentence names what is wrong with the typed value.
const SERVER_WORDED = new Set(['invalid_phone_number'])

const isText = (value: string | null | undefined): value is string =>
  typeof value === 'string' && value.trim().length > 0

// SA-755: an address is quoted only when it looks like one, so a crafted
// link cannot put arbitrary text into the banner.
const ADDRESS = /^[^\s@<>"]+@[^\s@<>"]+$/

export function isAddressShaped(value: string | null | undefined): value is string {
  return typeof value === 'string' && value.length <= 255 && ADDRESS.test(value)
}

export function connectErrorText(code: string, params: ConnectErrorParams = {}): string {
  if (code === 'account_mismatch') {
    const { expected, account, basis } = params
    if (isAddressShaped(expected) && isAddressShaped(account)) {
      if (basis === 'connected') {
        return `You signed in as ${account}, but this mailbox reads ${expected}. Nothing was saved. Connect again and choose ${expected}, or use Change account to read ${account} from now on.`
      }
      if (basis === 'typed') {
        return `You signed in as ${account}, but this mailbox's address is ${expected}. Nothing was saved. If mail to ${expected} arrives in ${account}'s inbox, connect as ${account}. Otherwise connect again and choose ${expected}.`
      }
      if (basis === 'named') {
        return `You signed in as ${account}, but you chose to connect ${expected}. Nothing was saved. Connect again and choose ${expected}.`
      }
      return `You signed in as ${account}, but this mailbox reads ${expected}. Nothing was saved. Connect again and choose ${expected}.`
    }
    return "You signed in as a different account than this mailbox reads. Nothing was saved. Connect again and choose the mailbox's account."
  }
  if (code === 'account_in_use') {
    const who = isText(params.otherName) ? `the mailbox "${params.otherName.trim()}"` : 'another mailbox'
    const account = isAddressShaped(params.account) ? params.account : 'That account'
    return `${account} is already read by ${who}. Nothing was saved. One account is read by one mailbox: to take mail for several addresses, keep that mailbox and route by To address in its routing rules.`
  }
  if (SERVER_WORDED.has(code) && isText(params.message)) return params.message.trim()
  return SENTENCES.get(code) ?? (isText(params.message) ? params.message.trim() : FALLBACK)
}

/** SA-947 (spec P11): a `{code, message}` refusal, worded; null when the
 *  error is not one (the caller keeps its own reading). */
export function refusalText(err: unknown): string | null {
  if (!(err instanceof ApiError)) return null
  const detail = err.detail
  if (!detail || typeof detail !== 'object' || Array.isArray(detail)) return null
  const { code, message } = detail as { code?: unknown; message?: unknown }
  if (typeof code !== 'string') return null
  return connectErrorText(code, { message: typeof message === 'string' ? message : null })
}

/** SA-1422: a `{code, …}` refusal's code, and the mailbox an `account_in_use` names
 *  (`other`, an id); null when the error is not one. */
export function refusalDetail(err: unknown): { code: string; other: string | null } | null {
  if (!(err instanceof ApiError)) return null
  const detail = err.detail
  if (!detail || typeof detail !== 'object' || Array.isArray(detail)) return null
  const { code, other } = detail as { code?: unknown; other?: unknown }
  if (typeof code !== 'string') return null
  return { code, other: typeof other === 'string' ? other : null }
}

/** SA-1422: the action a basis-specific refusal offers; null when it offers none. Offered only
 *  when both addresses are address-shaped, as the basis-specific sentence is, so the generic
 *  sentence never sits beside a button it does not mention. */
export function refusalAction(
  code: string,
  params: ConnectErrorParams = {},
): { kind: 'connect_as' | 'change_account'; account: string } | null {
  if (code !== 'account_mismatch') return null
  if (!isAddressShaped(params.account) || !isAddressShaped(params.expected)) return null
  if (params.basis === 'typed') return { kind: 'connect_as', account: params.account }
  if (params.basis === 'connected' || params.basis === 'named') {
    return { kind: 'change_account', account: params.account }
  }
  return null
}
